import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import round from "lodash.round";

import { OrderStatusOption, type Order } from "@fitness-app/data-models/entities/Order";

import { type ResolvedRange, resolveRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";
import { ALL_PRODUCTS_ITEM_ID } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import { supabase } from "~/store/initializeStore";

const ORDER_COLUMNS = "id, status, connectedSubscription, product, price, priceId, session";

const fetchOrders = async (products: string[], [start, end]: ResolvedRange) => {
  let query = supabase
    .from("product_order")
    .select(ORDER_COLUMNS)
    .gte("createdAt", start.toISOString())
    .lte("createdAt", end.toISOString())
    .order("createdAt", { ascending: false });

  const isAllProducts = products.length === 1 && products[0] === ALL_PRODUCTS_ITEM_ID;

  if (!isAllProducts) {
    query = query.in("product", products);
  }

  const { data } = await query.overrideTypes<Order[]>().throwOnError();

  return data;
};

const rangeKey = (range: ResolvedRange | null) => range?.map((date) => date.toISOString()) ?? null;

const sumPaid = (orders: Order[]) => orders.reduce((acc, order) => acc + (order.price?.amount || 0) / 100, 0);

export const useOrdersData = (products: string[], range: DateRange, comparisonRange: ResolvedRange | null) => {
  const currentRange = resolveRange(range);

  const { data, isLoading } = useQuery(["useOrdersData", products, rangeKey(currentRange)], {
    enabled: !!products.length && !!currentRange,
    keepPreviousData: true,
    queryFn: () => fetchOrders(products, currentRange!),
  });

  const { data: previousPeriodData } = useQuery(["usePreviousOrdersData", products, rangeKey(comparisonRange)], {
    enabled: !!products.length && !!comparisonRange,
    keepPreviousData: true,
    queryFn: () => fetchOrders(products, comparisonRange!),
  });

  const purchased = useMemo(() => data?.filter((order) => order.status === OrderStatusOption.Paid) || [], [data]);
  const purchasedPrevious = useMemo(
    () => previousPeriodData?.filter((order) => order.status === OrderStatusOption.Paid) || [],
    [previousPeriodData],
  );
  const totalAmount = useMemo(() => sumPaid(purchased), [purchased]);
  const totalAmountPrevious = useMemo(() => sumPaid(purchasedPrevious), [purchasedPrevious]);

  return {
    isLoading,
    orders: data,
    finalized: purchased,
    finalizedPrevious: purchasedPrevious,
    totalAmount,
    totalAmountPrevious,
    salesChange: purchasedPrevious.length ? round((purchased.length / purchasedPrevious.length) * 100 - 100) : null,
    totalAmountChange: totalAmountPrevious ? round(totalAmount - totalAmountPrevious) : null,
    totalAmountChangePercentage: totalAmountPrevious ? round((totalAmount / totalAmountPrevious) * 100 - 100) : null,
  };
};
